import { AxiosResponse } from 'axios';
import { UserInfo, UserRole } from '../components/admin/namespace/types';
import { Role } from '../components/admin/tenant/api/roles';
import { AuthAdminResponse, AuthInfo } from '../context/auth';
import { getApiUrl } from '../util/util';
import * as http from './http';

export enum RoleAbilityType {
	TenantAdministrator,
	EntityCreate,
	EntityGet,
	EntityUpdate,
}

export interface SsoConfig {
	ssoEnabled: boolean;
	ssoVersion: 'v1' | 'v0';
	ssoMigrationEnabled?: boolean;
}

export enum AbilityAuthorizationMetadataType {
	ProductEntity,
}

export interface AbilityAuthorizationMetadata {
	type: AbilityAuthorizationMetadataType;
}

export interface ProductEntityAbilityAuthorizationMetadata
	extends AbilityAuthorizationMetadata {
	productId?: string;
	// entityType: Entities.Type;
	entityType: string; // changed to string for now
}

export interface AbilityAuthorization {
	authorized: boolean;
	roleAbilityType: RoleAbilityType;
	metadata?: ProductEntityAbilityAuthorizationMetadata;
}

export interface AbilityAuthorization {
	authorized: boolean;
	roleAbilityType: RoleAbilityType;
	metadata?: ProductEntityAbilityAuthorizationMetadata;
}

export interface AuthenticateRequest {
	username: string;
	tenantId?: string;
	password: string;
	hostName?: string;
}

export interface ChangePasswordRequest {
	oldPassword: string;
	password: string;
	logout?: boolean;
}

export interface AccountV1Response {
	id: string;
	userLocator: string;
	accountType: string;
	roles: Role[];
	tenant: string;
	name: string;
	username: string;
	createdTimestamp: string;
	deleted: boolean;
	locked: boolean;
}

interface AuthApi {
	authenticateInternal(
		request: AuthenticateRequest,
	): Promise<AxiosResponse<AuthInfo>>;
	authenticateAdmin(
		request: AuthenticateRequest,
	): Promise<AxiosResponse<AuthAdminResponse>>;
	reAuthenticateInternal(
		authorizationToken: string,
	): Promise<AxiosResponse<AuthInfo>>;
	fetchUnifiedLogin(
		loginApiUrl: string,
		code: string,
	): Promise<AxiosResponse<AuthInfo>>;
	getTenantId(hostName?: string): Promise<TenantIdResponse>;
	getLoggedInAccount(): Promise<AuthInfo>;
	getUserInfo(): Promise<UserInfo>;
	getUserRoles(userUuid: string): Promise<UserRole[]>;
	activateUserRole(
		userUuid: string,
		role: string,
		accountType: string,
		jwt?: string,
	): Promise<AxiosResponse<any>>;
	getSsoFlag(): Promise<any>;
	getAccount(userLocator: string, jwt?: string): Promise<AccountV1Response>;
	getOauthLogout(): Promise<any>;
	changeUserPassword(
		authorizationToken: string,
		request: ChangePasswordRequest,
	): Promise<any>;
}

export default {
	getTenantId(hostname = __TENANT_HOSTNAME__) {
		const url = `${getApiUrl()}/tenant/v1/findByHostname?hostname=${hostname}`;
		return http.get(url).then(({ data }) => data);
	},
	async authenticateInternal(request: AuthenticateRequest) {
		const data: AuthenticateRequest = {
			username: request.username,
			password: request.password,
		};
		if (request.tenantId) {
			data.tenantId = request.tenantId; // can lookup w/ optional host name
		} else {
			Object.assign(data, await this.getTenantId(request.hostName));
		}
		const url = `${getApiUrl()}/account/v1/authenticateInternal`;
		const response = await http.post(url, undefined, data);
		return response;
	},

	async authenticateAdmin(request: AuthenticateRequest) {
		const data: AuthenticateRequest = {
			username: request.username,
			password: request.password,
		};
		const url = `${getApiUrl()}/account/v1/authenticateInternal`;
		const response = await http.post(url, undefined, data);
		return response;
	},
	reAuthenticateInternal(jwt: string) {
		const url = `${getApiUrl()}/account/v1/authorization/renewInternal`;
		return http.post(url, jwt);
	},
	fetchUnifiedLogin(loginApiUrl: string, code: string) {
		return http.put(`${loginApiUrl}/auth_tokens/${code}`);
	},
	getSsoFlag() {
		// if __SSO_HOSTNAME specified, the auth mode will be determined by checking against the hostname
		if (!!__SSO_HOSTNAME__) {
			let ssoConfig: SsoConfig;
			if (window.location.hostname === __SSO_HOSTNAME__) {
				ssoConfig = {
					ssoEnabled: true,
					ssoVersion: 'v1',
					ssoMigrationEnabled: true,
				};
				console.log('hostname', window.location.hostname, 'runs in SSO mode');
			} else {
				ssoConfig = {
					ssoEnabled: false,
					ssoVersion: 'v1',
				};
				console.log(
					'hostname',
					window.location.hostname,
					'runs in non-SSO mode',
				);
			}
			return Promise.resolve(ssoConfig);
		}
		// Can specify the SSO configuration statically via env variable instead of fetching via API call
		if (!!__SSO_CONFIG__) {
			return Promise.resolve(JSON.parse(__SSO_CONFIG__));
		}

		const url = `${getApiUrl()}/account/sso/config`;
		return http.get(url).then(({ data }) => data);
	},
	getAccount(userLocator: string, jwt?: string) {
		const url = `${getApiUrl()}/account/v1/account/${userLocator}`;
		return http.get(url, jwt).then(({ data }) => data);
	},
	getLoggedInAccount() {
		const url = `${getApiUrl()}/account/sso/loggedInAccount`;
		return http.getSso(url).then(({ data }) => data);
	},
	getUserInfo() {
		const url = `${getApiUrl()}/auth/users/whoami`;
		return http.getSso(url).then(({ data }) => data);
	},
	getUserRoles(userUuid: string) {
		const url = `${getApiUrl()}/auth/users/${userUuid}/roles`;
		return http.getSso(url).then(({ data }) => data);
	},
	activateUserRole(
		userUuid: string,
		role: string,
		accountType: string,
		jwt?: string,
	) {
		const url = `${getApiUrl()}/auth/users/${userUuid}/activateRole`;
		return http.post(
			url,
			jwt,
			{ role, accountType },
			{},
			{
				'Socotra-Ontologyuri-Expanded': 'true',
			},
		);
	},
	getOauthLogout() {
		const url = `${getApiUrl()}/oauth/logout`;
		return http.getSso(url).then(({ data }) => data);
	},
	changeUserPassword(
		authorizationToken: string,
		request: ChangePasswordRequest,
	) {
		const url = `${getApiUrl()}/account/v1/accounts/password`;
		const data: ChangePasswordRequest = {
			oldPassword: request.oldPassword,
			password: request.password,
			logout: request.logout ? request.logout : false,
		};
		return http.patch(url, authorizationToken, data);
	},
} as AuthApi;
